import { __awaiter, __rest } from "tslib";
import { Auth0Client } from "@auth0/auth0-spa-js";
import { gql, ApolloClient, InMemoryCache, HttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context/index.js";
const CODE_RE = /[?&]code=[^&]+/;
const STATE_RE = /[?&]state=[^&]+/;
const ERROR_RE = /[?&]error=[^&]+/;
class AuthManager {
  /**
   * @param config - Photon AuthManager configuration options
   * @remarks - Note, that organization is optional for scenarios in which a provider supports more than themselves.
   */
  constructor({ authentication, organization, audience = "https://api.photon.health", connection }) {
    this._getAccessToken = (...args_1) => __awaiter(this, [...args_1], void 0, function* ({ audience: audience2 } = {
      audience: this.audience
    }, throwIfFailure = false) {
      const opts = {
        authorizationParams: Object.assign({ audience: audience2 || this.audience || void 0 }, this.organization ? { organization: this.organization } : {})
      };
      let token;
      try {
        token = yield this.authentication.getTokenSilently(opts);
      } catch (e) {
        if (e.message.includes("Consent required")) {
          token = yield this.authentication.getTokenWithPopup(opts);
        }
      }
      if (!token) {
        const redirectOpts = Object.assign(Object.assign({}, opts), { appState: {
          returnTo: `${window.location.pathname}${window.location.search}`
        } });
        yield this.authentication.loginWithRedirect(redirectOpts);
        if (throwIfFailure) {
          throw new Error("Unable to retrieve access token");
        } else {
          return yield this._getAccessToken({ audience: audience2 }, true);
        }
      }
      return token;
    });
    this.authentication = authentication;
    this.organization = organization;
    this.audience = audience;
    this.connection = connection;
  }
  /**
   * Performs a login against the specified Auth0 domain
   * @param config - Login configuration
   * @returns
   */
  login(_a2) {
    return __awaiter(this, arguments, void 0, function* ({ organizationId, invitation, connection, appState }) {
      const opts = Object.assign({ authorizationParams: Object.assign(Object.assign(Object.assign(Object.assign({}, this.audience ? { audience: this.audience } : {}), connection || this.connection ? { connection: connection || this.connection } : {}), organizationId || this.organization ? { organization: organizationId || this.organization } : {}), invitation ? { invitation } : {}) }, appState ? { appState } : {});
      return this.authentication.loginWithRedirect(opts);
    });
  }
  /**
   * Performs a logout against the specified Auth0 domain
   * @param config - Logout configuration
   * @returns
   */
  logout(_a2) {
    return __awaiter(this, arguments, void 0, function* ({ returnTo, federated = false }) {
      const opts = {
        logoutParams: Object.assign(Object.assign({}, returnTo ? { returnTo } : {}), federated ? { federated } : {})
      };
      return this.authentication.logout(opts);
    });
  }
  /**
   * Determines if URL has Auth0 parameters
   * @returns boolean
   */
  hasAuthParams(searchParams = window.location.search) {
    return (CODE_RE.test(searchParams) || ERROR_RE.test(searchParams)) && STATE_RE.test(searchParams);
  }
  /**
   * Retrieves a valid access token
   * @param config - getAccessToken configuration
   * @returns
   */
  getAccessToken() {
    return __awaiter(this, arguments, void 0, function* ({ audience } = {
      audience: this.audience
    }) {
      return yield this._getAccessToken({ audience: audience !== null && audience !== void 0 ? audience : this.audience });
    });
  }
  /**
   * Retrieves a valid access token
   * @param config - getAccessToken configuration
   * @returns
   */
  getAccessTokenWithConsent() {
    return __awaiter(this, arguments, void 0, function* ({ audience } = {
      audience: this.audience
    }) {
      const opts = {
        authorizationParams: Object.assign({ audience: audience || this.audience || void 0 }, this.organization ? { organization: this.organization } : {})
      };
      return this.authentication.getTokenWithPopup(opts);
    });
  }
  /**
   * Silently performs a getAccessToken and pre-populates the token and user information caches
   * @returns
   */
  checkSession() {
    return __awaiter(this, void 0, void 0, function* () {
      const opts = {
        authorizationParams: Object.assign({ audience: this.audience || void 0 }, this.organization ? { organization: this.organization } : {})
      };
      return this.authentication.checkSession(opts);
    });
  }
  /**
   * Handes Auth0 redirect after login
   * @param url - The url which contains the code and state parameters (defaults to window.location.href)
   * @returns
   */
  handleRedirect(url) {
    return __awaiter(this, void 0, void 0, function* () {
      try {
        return this.authentication.handleRedirectCallback(url);
      } catch (err) {
        console.error(err);
      }
    });
  }
  /**
   * Retrieves information about the currently authenticated user
   * @returns
   */
  getUser() {
    return __awaiter(this, void 0, void 0, function* () {
      return this.authentication.getUser();
    });
  }
  /**
   * Determines whether or not a user is currently logged in
   * @returns
   */
  isAuthenticated() {
    return __awaiter(this, void 0, void 0, function* () {
      try {
        yield this.authentication.checkSession();
        return yield this.authentication.isAuthenticated();
      } catch (_error) {
        return false;
      }
    });
  }
}
const ORGANIZATION_FIELDS = gql`
  fragment OrganizationFields on Organization {
    id
    name
  }
`;
const PATIENT_FIELDS = gql`
  fragment PatientFields on Patient {
    id
    externalId
    name {
      full
    }
    dateOfBirth
    sex
    gender
    email
    phone
    address {
      id
      name {
        full
      }
      city
      country
      postalCode
      state
      street1
      street2
    }
  }
`;
const ALLERGEN_FIELDS = gql`
  fragment AllergenFields on Allergen {
    id
    name
    rxcui
  }
`;
const CATALOG_FIELDS = gql`
  fragment CatalogFields on Catalog {
    id
    name
  }
`;
const MEDICATION_FIELDS = gql`
  fragment MedicationFields on Medication {
    id
    name
    form
    schedule
    controlled
  }
`;
const SEARCH_MEDICATION_FIELDS = gql`
  fragment SearchMedicationFields on SearchMedication {
    id
    name
  }
`;
const MEDICAL_EQUIPMENT_FIELDS = gql`
  fragment MedicalEquipmentFields on MedicalEquipment {
    id
    name
  }
`;
const CATALOG_TREATMENT_FIELDS = gql`
  fragment CatalogTreatmentFields on Catalog {
    treatments {
      id
      name
    }
  }
`;
const DISPENSE_UNIT_FIELDS = gql`
  fragment DispenseUnitFields on DispenseUnit {
    name
  }
`;
const FILL_PRESCRIPTION_FIELDS = gql`
  fragment FillPrescriptionFields on Fill {
    id
    state
    requestedAt
    filledAt
    order {
      id
      createdAt
      state
      fulfillment {
        state
      }
      pharmacy {
        id
        name
        address {
          street1
          street2
          city
          state
          postalCode
        }
      }
    }
  }
`;
const PRESCRIPTION_FIELDS = gql`
  ${FILL_PRESCRIPTION_FIELDS}
  fragment PrescriptionFields on Prescription {
    id
    externalId
    fills {
      ...FillPrescriptionFields
    }
    prescriber {
      id
      name {
        full
      }
    }
    patient {
      id
      name {
        full
      }
    }
    state
    treatment {
      id
      name
    }
    dispenseAsWritten
    dispenseQuantity
    dispenseUnit
    fillsAllowed
    fillsRemaining
    daysSupply
    instructions
    notes
    effectiveDate
    doNotFillBeforeDate
    expirationDate
    writtenAt
  }
`;
const FILL_FIELDS = gql`
  fragment FillFields on Fill {
    id
    prescription {
      id
    }
    treatment {
      name
    }
    state
    requestedAt
    filledAt
  }
`;
const ORDER_FIELDS = gql`
  ${PATIENT_FIELDS}
  ${FILL_FIELDS}
  fragment OrderFields on Order {
    id
    externalId
    state
    fills {
      ...FillFields
    }
    patient {
      ...PatientFields
    }
    pharmacy {
      id
      name
      phone
      address {
        city
        country
        postalCode
        state
        street1
        street2
      }
    }
    fulfillment {
      type
      state
      carrier
      trackingNumber
    }
    createdAt
  }
`;
const WEBHOOK_CONFIG_FIELDS = gql`
  fragment WebhookFields on WebhookConfig {
    id
    name
    filters
    url
  }
`;
const CLIENT_FIELDS = gql`
  fragment ClientFields on Client {
    id
    name
    secret
    appType
  }
`;
const PHARMACY_FIELDS = gql`
  fragment PharmacyFields on Pharmacy {
    id
    NPI
    NCPDP
    name
    fulfillmentTypes
    address {
      city
      country
      postalCode
      state
      street1
      street2
    }
  }
`;
const PRESCRIPTION_TEMPLATE_FIELDS = gql`
  fragment PrescriptionTemplateFields on PrescriptionTemplate {
    id
    daysSupply
    dispenseAsWritten
    dispenseQuantity
    dispenseUnit
    instructions
    notes
    fillsAllowed
    treatment {
      id
      name
    }
    isPrivate
  }
`;
const fragments = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  ALLERGEN_FIELDS,
  CATALOG_FIELDS,
  CATALOG_TREATMENT_FIELDS,
  CLIENT_FIELDS,
  DISPENSE_UNIT_FIELDS,
  FILL_FIELDS,
  FILL_PRESCRIPTION_FIELDS,
  MEDICAL_EQUIPMENT_FIELDS,
  MEDICATION_FIELDS,
  ORDER_FIELDS,
  ORGANIZATION_FIELDS,
  PATIENT_FIELDS,
  PHARMACY_FIELDS,
  PRESCRIPTION_FIELDS,
  PRESCRIPTION_TEMPLATE_FIELDS,
  SEARCH_MEDICATION_FIELDS,
  WEBHOOK_CONFIG_FIELDS
}, Symbol.toStringTag, { value: "Module" }));
function makeQuery(apollo_1, query_1) {
  return __awaiter(this, arguments, void 0, function* (apollo, query, variables = {}, fetchPolicy = "network-only") {
    const result = yield apollo.query({
      query,
      variables,
      fetchPolicy
    });
    return Object.assign(Object.assign({}, result), { refetch: (vars) => apollo.query({ query, variables: Object.assign(variables, vars) }), fetchMore: ({ after, first }) => apollo.query({
      query,
      variables: Object.assign(variables, { after, first })
    }) });
  });
}
function makeMutation(apollo, mutation) {
  return ({ variables, refetchQueries, awaitRefetchQueries = false }) => {
    return apollo.mutate({
      mutation,
      refetchQueries,
      awaitRefetchQueries,
      variables
    });
  };
}
const clinicalAppUrl = {
  tau: "https://app.boson.health",
  boson: "https://app.boson.health",
  neutron: "https://app.neutron.health",
  photon: "https://app.photon.health"
};
const lambdasApiUrl = {
  tau: "https://api.boson.health",
  boson: "https://api.boson.health",
  neutron: "https://api.neutron.health",
  photon: "https://api.photon.health"
};
const clinicalApiUrl = {
  tau: "http://clinical-api.tau.health:8080",
  boson: "https://clinical-api.boson.health",
  neutron: "https://clinical-api.neutron.health",
  photon: "https://clinical-api.photon.health"
};
function getClinicalUrl(uri) {
  const foundService = Object.keys(clinicalAppUrl).find((service) => uri.toLowerCase().includes(service));
  return clinicalAppUrl[foundService || "photon"];
}
class CatalogQueryManager {
  /**
   * @param apollo - An Apollo client instance
   */
  constructor(apollo) {
    this.apollo = apollo;
  }
  /**
   * Retrieves all catalogs based on currently authenticated organization
   * @param options - Query options
   * @returns
   */
  getCatalogs() {
    return __awaiter(this, arguments, void 0, function* ({ fragment } = {
      fragment: { CatalogFields: CATALOG_FIELDS }
    }) {
      if (!fragment) {
        fragment = { CatalogFields: CATALOG_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_CATALOGS = gql`
      ${fValue}
      query catalogs {
        catalogs {
          ...${fName}
        }
      }
    `;
      return makeQuery(this.apollo, GET_CATALOGS);
    });
  }
  /**
   * Retrieves catalog by id
   * @param options - Query options
   * @returns
   */
  getCatalog() {
    return __awaiter(this, arguments, void 0, function* ({ id, fragment } = {
      id: "",
      fragment: { CatalogFields: CATALOG_FIELDS }
    }) {
      if (!fragment) {
        fragment = { CatalogFields: CATALOG_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_CATALOG = gql`
      ${fValue}
      query catalog($id: ID) {
        catalog(id: $id) {
          ...${fName}
        }
      }
    `;
      return makeQuery(this.apollo, GET_CATALOG, { id });
    });
  }
  /**
   * Adds a treatment to a catalog
   * @param options - Query options
   * @returns
   */
  addToCatalog({ fragment }) {
    if (!fragment) {
      fragment = { MedicationFields: MEDICATION_FIELDS };
    }
    const [fName, fValue] = Object.entries(fragment)[0];
    const ADD_TO_CATALOG = gql`
      ${fValue}
      mutation addToCatalog(
        $catalogId: ID!
        $treatmentId: ID!
      ) {
        addToCatalog(
          catalogId: $catalogId
          treatmentId: $treatmentId
        ) {
          ...${fName}
        }
      }
    `;
    return makeMutation(this.apollo, ADD_TO_CATALOG);
  }
  /**
   * Removes a treatment from a catalog
   * @param options - Query options
   * @returns
   */
  removeFromCatalog({ fragment }) {
    if (!fragment) {
      fragment = { MedicationFields: MEDICATION_FIELDS };
    }
    const [fName, fValue] = Object.entries(fragment)[0];
    const REMOVE_FROM_CATALOG = gql`
      ${fValue}
      mutation removeFromCatalog(
        $catalogId: ID!
        $treatmentId: ID!
      ) {
        removeFromCatalog(
          catalogId: $catalogId
          treatmentId: $treatmentId
        ) {
          ...${fName}
        }
      }
    `;
    return makeMutation(this.apollo, REMOVE_FROM_CATALOG);
  }
}
class MedicationQueryManager {
  /**
   * @param apollo - An Apollo client instance
   */
  constructor(apollo) {
    this.apollo = apollo;
  }
  /**
   * Retrieves all medications, optionally filtered by name, type, and code
   * @param options - Query options
   * @returns
   */
  getMedications() {
    return __awaiter(this, arguments, void 0, function* ({ filter, after, first, fragment } = {
      fragment: { MedicationFields: MEDICATION_FIELDS }
    }) {
      if (!fragment) {
        fragment = { MedicationFields: MEDICATION_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_MEDICATIONS = gql`
      ${fValue}
      query medications($filter: MedicationFilter, $after: ID, $first: Int) {
        medications(filter: $filter, after: $after, first: $first) {
          ...${fName}
    }
  }
    `;
      return makeQuery(this.apollo, GET_MEDICATIONS, {
        filter,
        after,
        first
      });
    });
  }
}
class MedicalEquipmentQueryManager {
  /**
   * @param apollo - An Apollo client instance
   */
  constructor(apollo) {
    this.apollo = apollo;
  }
  /**
   * Retrieves all medical equipment, optionally filtered by name, type, and code
   * @param options - Query options
   * @returns
   */
  getMedicalEquipment() {
    return __awaiter(this, arguments, void 0, function* ({ name, after, first, fragment } = {
      fragment: { MedicalEquipmentFields: MEDICAL_EQUIPMENT_FIELDS }
    }) {
      if (!fragment) {
        fragment = { MedicalEquipmentFields: MEDICAL_EQUIPMENT_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_MEDICAL_EQUIPMENT = gql`
      ${fValue}
      query medicalEquipment($name: String, $after: ID, $first: Int) {
        medicalEquipment(name: $name, after: $after, first: $first) {
          ...${fName}
    }
  }
    `;
      return makeQuery(this.apollo, GET_MEDICAL_EQUIPMENT, {
        name,
        after,
        first
      });
    });
  }
}
class OrderQueryManager {
  /**
   * @param apollo - An Apollo client instance
   */
  constructor(apollo) {
    this.apollo = apollo;
  }
  /**
   * Retrieves all orders in a paginated fashion, optionally filtered by patientId, patientName
   * @param options - Query options
   * @returns
   */
  getOrders() {
    return __awaiter(this, arguments, void 0, function* ({ patientId, patientName, after, first, fragment } = {
      first: 25,
      fragment: { OrderFields: ORDER_FIELDS }
    }) {
      if (!first) {
        first = 25;
      }
      if (!fragment) {
        fragment = { OrderFields: ORDER_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_ORDERS = gql`
        ${fValue}
        query orders(
          $patientId: ID
          $patientName: String
          $after: ID
          $first: Int
      ) {
          orders(
              filter: {
                  patientId: $patientId
                  patientName: $patientName
              }
              after: $after
              first: $first
          ) {
              ...${fName}
          }
        }
      `;
      return makeQuery(this.apollo, GET_ORDERS, {
        patientId,
        patientName,
        after,
        first
      });
    });
  }
  /**
   * Retrieves order by id
   * @param options - Query options
   * @returns
   */
  getOrder() {
    return __awaiter(this, arguments, void 0, function* ({ id, fragment } = {
      id: "",
      fragment: { OrderFields: ORDER_FIELDS }
    }) {
      if (!fragment) {
        fragment = { OrderFields: ORDER_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_ORDER = gql`
        ${fValue}
        query order($id: ID!) {
          order(id: $id) {
            ...${fName}
          }
        }
      `;
      return makeQuery(this.apollo, GET_ORDER, { id });
    });
  }
  /**
   * Creates a new order
   * @param options - Query options
   * @returns
   */
  createOrder({ fragment }) {
    if (!fragment) {
      fragment = { OrderFields: ORDER_FIELDS };
    }
    const [fName, fValue] = Object.entries(fragment)[0];
    const CREATE_ORDER = gql`
      ${fValue}
      mutation createOrder(
        $externalId: ID
        $patientId: ID!
        $fills: [FillInput!]!
        $addressId: ID
        $address: AddressInput
        $pharmacyId: ID
        $groupId: ID
        $supervisorId: ID
      ) {
        createOrder(
          externalId: $externalId
          patientId: $patientId
          fills: $fills
          addressId: $addressId
          address: $address
          pharmacyId: $pharmacyId
          groupId: $groupId
          supervisorId: $supervisorId
        ) {
          ...${fName}
        }
      }
    `;
    return makeMutation(this.apollo, CREATE_ORDER);
  }
}
class PatientQueryManager {
  /**
   * @param apollo - An Apollo client instance
   */
  constructor(apollo) {
    this.apollo = apollo;
  }
  /**
   * Retrieves patient by id
   * @param options - Query options
   * @returns
   */
  getPatient() {
    return __awaiter(this, arguments, void 0, function* ({ id, fragment } = {
      id: "",
      fragment: { PatientFields: PATIENT_FIELDS }
    }) {
      if (!fragment) {
        fragment = { PatientFields: PATIENT_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_PATIENT = gql`
      ${fValue}
      query patient($id: ID!) {
        patient(id: $id) {
          ...${fName}
        }
      }
    `;
      return makeQuery(this.apollo, GET_PATIENT, { id });
    });
  }
  /**
   * Retrieves all patients in a paginated fashion, optionally filtered by name
   * @param options - Query options
   * @returns
   */
  getPatients() {
    return __awaiter(this, arguments, void 0, function* ({ after, first, name, fragment } = {
      first: 25,
      fragment: { PatientFields: PATIENT_FIELDS }
    }) {
      if (!fragment) {
        fragment = { PatientFields: PATIENT_FIELDS };
      }
      if (!first) {
        first = 25;
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_PATIENTS = gql`
      ${fValue}
      query patients($after: ID, $name: String, $first: Int) {
        patients(after: $after, first: $first, filter: { name: $name }) {
          ...${fName}
        }
      }
    `;
      return makeQuery(this.apollo, GET_PATIENTS, {
        after,
        name,
        first
      });
    });
  }
  /**
   * Creates a new patient
   * @param options - Query options
   * @returns
   */
  createPatient({ fragment }) {
    if (!fragment) {
      fragment = { PatientFields: PATIENT_FIELDS };
    }
    const [fName, fValue] = Object.entries(fragment)[0];
    const CREATE_PATIENT = gql`
      ${fValue}
      mutation createPatient(
        $externalId: ID
        $name: NameInput!
        $dateOfBirth: AWSDate!
        $sex: SexType!
        $gender: String
        $email: AWSEmail
        $phone: AWSPhone!
        $address: AddressInput
        $allergies: [AllergenInput]
        $medicationHistory: [MedHistoryInput]
        $preferredPharmacies: [ID]
      ) {
        createPatient(
          externalId: $externalId
          name: $name
          dateOfBirth: $dateOfBirth
          sex: $sex
          gender: $gender
          address: $address
          email: $email
          phone: $phone
          allergies: $allergies
          medicationHistory: $medicationHistory
          preferredPharmacies: $preferredPharmacies
        ) {
          ...${fName}
        }
      }`;
    return makeMutation(this.apollo, CREATE_PATIENT);
  }
  /**
   * Updates an existing patient
   * @param options - Query options
   * @returns
   */
  updatePatient({ fragment }) {
    if (!fragment) {
      fragment = { PatientFields: PATIENT_FIELDS };
    }
    const [fName, fValue] = Object.entries(fragment)[0];
    const UPDATE_PATIENT = gql`
      ${fValue}
      mutation updatePatient(
        $id: ID!
        $externalId: ID
        $name: NameInput
        $dateOfBirth: AWSDate
        $sex: SexType
        $gender: String
        $email: AWSEmail
        $phone: AWSPhone
        $allergies: [AllergenInput]
        $medicationHistory: [MedHistoryInput]
        $address: AddressInput
        $preferredPharmacies: [ID]
      ) {
        updatePatient(
          id: $id
          externalId: $externalId
          name: $name
          dateOfBirth: $dateOfBirth
          sex: $sex
          gender: $gender
          email: $email
          phone: $phone
          allergies: $allergies
          medicationHistory: $medicationHistory
          address: $address,
          preferredPharmacies: $preferredPharmacies
        ) {
          ...${fName}
        }
      }`;
    return makeMutation(this.apollo, UPDATE_PATIENT);
  }
  /**
   * Adds a preferred pharmcy to a patient
   * @param options - Query options
   * @returns
   */
  removePatientPreferredPharmacy({ fragment }) {
    if (!fragment) {
      fragment = { PatientFields: PATIENT_FIELDS };
    }
    const [fName, fValue] = Object.entries(fragment)[0];
    const REMOVE_PATIENT_PREFERRED_PHARM = gql`
      ${fValue}
      mutation removePatientPreferredPharmacy(
        $patientId: ID!
        $pharmacyId: ID!
      ) {
        removePatientPreferredPharmacy(
          patientId: $patientId
          pharmacyId: $pharmacyId
        ) {
          ...${fName}
        }
      }`;
    return makeMutation(this.apollo, REMOVE_PATIENT_PREFERRED_PHARM);
  }
  /**
   * Removes an allergen from a patient
   * @param options - Query options
   * @returns
   */
  removePatientAllergy({ fragment }) {
    if (!fragment) {
      fragment = { PatientFields: PATIENT_FIELDS };
    }
    const [fName, fValue] = Object.entries(fragment)[0];
    const REMOVE_PATIENT_ALLERGY = gql`
      ${fValue}
      mutation removePatientAllergy(
        $id: ID!
        $allergenId: ID!
      ) {
        updatePatient(
          id: $id
          allergenId: $allergenId
        ) {
          ...${fName}
        }
      }`;
    return makeMutation(this.apollo, REMOVE_PATIENT_ALLERGY);
  }
}
class PharmacyQueryManager {
  /**
   * @param apollo - An Apollo client instance
   */
  constructor(apollo) {
    this.apollo = apollo;
  }
  /**
   * Retrieves all pharmacies, optionally filtered by pharmacy name, in a given latitude/longitude/radius
   * @param options - Query options
   * @returns
   */
  getPharmacies() {
    return __awaiter(this, arguments, void 0, function* ({ name, location, type, after, first, fragment } = {
      first: 100,
      fragment: { PharmacyFields: PHARMACY_FIELDS }
    }) {
      if (!first) {
        first = 100;
      }
      if (!fragment) {
        fragment = { PharmacyFields: PHARMACY_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_PHARMACIES = gql`
      ${fValue}
      query pharmacies($name: String, $location: LatLongSearch, $type: FulfillmentType, $after: Int, $first: Int) {
        pharmacies(name: $name, location: $location, type: $type, after: $after, first: $first) {
          ...${fName}
        }
      }
    `;
      return makeQuery(this.apollo, GET_PHARMACIES, {
        name,
        location,
        type,
        after,
        first
      });
    });
  }
  /**
   * Retrieves pharmacy by id
   * @param options - Query options
   * @returns
   */
  getPharmacy() {
    return __awaiter(this, arguments, void 0, function* ({ id, fragment } = {
      id: "",
      fragment: { PharmacyFields: PHARMACY_FIELDS }
    }) {
      if (!fragment) {
        fragment = { PharmacyFields: PHARMACY_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_PHARMACY = gql`
          ${fValue}
          query pharmacy($id: ID!) {
            pharmacy(id: $id) {
              ...${fName}
            }
          }
        `;
      return makeQuery(this.apollo, GET_PHARMACY, { id });
    });
  }
}
class PrescriptionQueryManager {
  /**
   * @param apollo - An Apollo client instance
   */
  constructor(apollo) {
    this.apollo = apollo;
  }
  getPrescriptions() {
    return __awaiter(this, arguments, void 0, function* ({ patientId, patientName, prescriberId, state, after, first, fragment } = {
      first: 25,
      fragment: { PrescriptionFields: PRESCRIPTION_FIELDS }
    }) {
      if (!fragment) {
        fragment = { PrescriptionFields: PRESCRIPTION_FIELDS };
      }
      if (!first) {
        first = 25;
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_PRESCRIPTIONS = gql`
      ${fValue}
      query prescriptions(
        $patientId: ID
        $patientName: String
        $prescriberId: ID
        $state: PrescriptionState
        $after: ID
        $first: Int
      ) {
        prescriptions(
          filter: {
            patientId: $patientId
            patientName: $patientName
            prescriberId: $prescriberId
            state: $state
          }
          after: $after
          first: $first
        ) {
          ...${fName}
        }
      }
    `;
      return makeQuery(this.apollo, GET_PRESCRIPTIONS, {
        patientId,
        patientName,
        prescriberId,
        state,
        after,
        first
      });
    });
  }
  getPrescription() {
    return __awaiter(this, arguments, void 0, function* ({ id, fragment } = {
      id: "",
      fragment: { PrescriptionFields: PRESCRIPTION_FIELDS }
    }) {
      if (!fragment) {
        fragment = { PrescriptionFields: PRESCRIPTION_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_PRESCRIPTION = gql`
      ${fValue}
      query prescription($id: ID!) {
        prescription(id: $id) {
          ...${fName}
        }
      }
    `;
      return makeQuery(this.apollo, GET_PRESCRIPTION, {
        id
      });
    });
  }
  getDispenseUnits() {
    return __awaiter(this, arguments, void 0, function* ({ fragment } = {
      fragment: { DispenseUnitFields: DISPENSE_UNIT_FIELDS }
    }) {
      if (!fragment) {
        fragment = { DispenseUnitFields: DISPENSE_UNIT_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_DISPENSE_UNITS = gql`
      ${fValue}
      query dispenseUnits {
        dispenseUnits {
          ...${fName}
        }
      }
    `;
      return makeQuery(this.apollo, GET_DISPENSE_UNITS, {});
    });
  }
  createPrescription({ fragment }) {
    if (!fragment) {
      fragment = { PrescriptionFields: PRESCRIPTION_FIELDS };
    }
    const [fName, fValue] = Object.entries(fragment)[0];
    const CREATE_PRESCRIPTION = gql`
      ${fValue}
      mutation createPrescription(
        $externalId: ID
        $patientId: ID!
        $treatmentId: ID!
        $dispenseAsWritten: Boolean
        $dispenseQuantity: Float!
        $dispenseUnit: String!
        $fillsAllowed: Int!
        $daysSupply: Int
        $instructions: String!
        $notes: String
        $doNotFillBeforeDate: AWSDate
        $diagnoses: [ID]
      ) {
        createPrescription(
          externalId: $externalId
          patientId: $patientId
          treatmentId: $treatmentId
          dispenseAsWritten: $dispenseAsWritten
          dispenseQuantity: $dispenseQuantity
          dispenseUnit: $dispenseUnit
          fillsAllowed: $fillsAllowed
          daysSupply: $daysSupply
          instructions: $instructions
          notes: $notes
          doNotFillBeforeDate: $doNotFillBeforeDate
          diagnoses: $diagnoses
        ) {
          ...${fName}
        }
      }`;
    return makeMutation(this.apollo, CREATE_PRESCRIPTION);
  }
}
class AllergenQueryManager {
  /**
   * @param apollo - An Apollo client instance
   */
  constructor(apollo) {
    this.apollo = apollo;
  }
  /**
   * Retrieves all allergens, optionally filtered by name (fuzzy)
   * @param options - Query options
   * @returns
   */
  getAllergens() {
    return __awaiter(this, arguments, void 0, function* ({ fragment, filter } = {
      fragment: { AllergenFields: ALLERGEN_FIELDS }
    }) {
      if (!fragment) {
        fragment = { AllergenFields: ALLERGEN_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_ALLERGENS = gql`
        ${fValue}
        query allergens($filter: AllergenFilter) {
            allergens(filter: $filter) {
                ...${fName}
            }
        }
      `;
      return makeQuery(this.apollo, GET_ALLERGENS, {
        filter
      });
    });
  }
}
class PrescriptionTemplateQueryManager {
  /**
   * @param apollo - An Apollo client instance
   */
  constructor(apollo) {
    this.apollo = apollo;
  }
  /**
   * Creates a new prescription template
   * @param options - Query options
   * @returns
   */
  createPrescriptionTemplate({ fragment }) {
    if (!fragment) {
      fragment = { PrescriptionTemplateFields: PRESCRIPTION_TEMPLATE_FIELDS };
    }
    const [fName, fValue] = Object.entries(fragment)[0];
    const CREATE_PRESCRIPTION_TEMPLATE = gql`
        ${fValue}
        mutation createPrescriptionTemplate(
          $catalogId: ID!,
          $treatmentId: ID!,
          $dispenseAsWritten: Boolean,
          $dispenseQuantity: Float,
          $dispenseUnit: String,
          $fillsAllowed: Int,
          $daysSupply: Int,
          $instructions: String,
          $notes: String,
          $name: String,
          $isPrivate: Boolean
        ) {
          createPrescriptionTemplate(
            catalogId: $catalogId
            treatmentId: $treatmentId
            dispenseAsWritten: $dispenseAsWritten
            dispenseQuantity: $dispenseQuantity
            dispenseUnit: $dispenseUnit
            fillsAllowed: $fillsAllowed
            daysSupply: $daysSupply
            instructions: $instructions
            notes: $notes
            name: $name
            isPrivate: $isPrivate
        ) {
            ...${fName}
        }
      }
      `;
    return makeMutation(this.apollo, CREATE_PRESCRIPTION_TEMPLATE);
  }
  /**
   * Updates a prescription template
   * @param options - Query options
   * @returns
   */
  updatePrescriptionTemplate({ fragment }) {
    if (!fragment) {
      fragment = { PrescriptionTemplateFields: PRESCRIPTION_TEMPLATE_FIELDS };
    }
    const [fName, fValue] = Object.entries(fragment)[0];
    const UPDATE_PRESCRIPTION_TEMPLATE = gql`
        ${fValue}
        mutation updatePrescriptionTemplate(
          $catalogId: ID!,
          $templateId: ID!,
          $dispenseAsWritten: Boolean,
          $dispenseQuantity: Float,
          $dispenseUnit: String,
          $fillsAllowed: Int,
          $daysSupply: Int,
          $instructions: String,
          $notes: String,
          $name: String
        ) {
          updatePrescriptionTemplate(
            catalogId: $catalogId
            templateId: $templateId
            dispenseAsWritten: $dispenseAsWritten
            dispenseQuantity: $dispenseQuantity
            dispenseUnit: $dispenseUnit
            fillsAllowed: $fillsAllowed
            daysSupply: $daysSupply
            instructions: $instructions
            notes: $notes
            name: $name
        ) {
            ...${fName}
        }
      }
      `;
    return makeMutation(this.apollo, UPDATE_PRESCRIPTION_TEMPLATE);
  }
  /**
   * Deletes an existing prescription template
   * @param options - Query options
   * @returns
   */
  deletePrescriptionTemplate({ fragment }) {
    if (!fragment) {
      fragment = { PrescriptionTemplateFields: PRESCRIPTION_TEMPLATE_FIELDS };
    }
    const [fName, fValue] = Object.entries(fragment)[0];
    const DELETE_PRESCRIPTION_TEMPLATE = gql`
        ${fValue}
        mutation deletePrescriptionTemplate(
          $catalogId: ID!,
          $templateId: ID!
        ) {
          deletePrescriptionTemplate(
            catalogId: $catalogId
            templateId: $templateId
        ) {
            ...${fName}
        }
      }
      `;
    return makeMutation(this.apollo, DELETE_PRESCRIPTION_TEMPLATE);
  }
}
class SearchMedicationQueryManager {
  /**
   * @param apollo - An Apollo client instance
   */
  constructor(apollo) {
    this.apollo = apollo;
  }
  /**
   * Retrieves all medication concepts, filtered by name
   * @param options - Query options
   * @returns
   */
  getConcepts(_a2) {
    return __awaiter(this, arguments, void 0, function* ({ name, fragment }) {
      if (!fragment) {
        fragment = { SearchMedicationFields: SEARCH_MEDICATION_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_CONCEPTS = gql`
        ${fValue}
        query medicationConcepts($name: String!) {
          medicationConcepts(name: $name) {
            ...${fName}
      }
    }
      `;
      return makeQuery(this.apollo, GET_CONCEPTS, {
        name
      });
    });
  }
  /**
   * Retrieves all strengths for a SearchMedication CONCEPT
   * @param options - Query options
   * @returns
   */
  getStrengths(_a2) {
    return __awaiter(this, arguments, void 0, function* ({ id, fragment }) {
      if (!fragment) {
        fragment = { SearchMedicationFields: SEARCH_MEDICATION_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_STRENGTHS = gql`
          ${fValue}
          query medicationStrengths($id: String!) {
            medicationStrengths(id: $id) {
              ...${fName}
        }
      }
        `;
      return makeQuery(this.apollo, GET_STRENGTHS, {
        id
      });
    });
  }
  /**
   * Retrieves all routes for a SearchMedication STRENGTH
   * @param options - Query options
   * @returns
   */
  getRoutes(_a2) {
    return __awaiter(this, arguments, void 0, function* ({ id, fragment }) {
      if (!fragment) {
        fragment = { SearchMedicationFields: SEARCH_MEDICATION_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_ROUTES = gql`
              ${fValue}
              query medicationRoutes($id: String!) {
                medicationRoutes(id: $id) {
                  ...${fName}
            }
          }
            `;
      return makeQuery(this.apollo, GET_ROUTES, {
        id
      });
    });
  }
  /**
   * Retrieves all forms for a SearchMedication ROUTE
   * @param options - Query options
   * @returns
   */
  getForms(_a2) {
    return __awaiter(this, arguments, void 0, function* ({ id, fragment }) {
      if (!fragment) {
        fragment = { MedicationFields: MEDICATION_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_FORMS = gql`
              ${fValue}
              query medicationForms($id: String!) {
                medicationForms(id: $id) {
                  ...${fName}
            }
          }
            `;
      return makeQuery(this.apollo, GET_FORMS, {
        id
      });
    });
  }
  /**
   * Retrieves all products for a SearchMedication FORM/Medication DRUG
   * @param options - Query options
   * @returns
   */
  getProducts(_a2) {
    return __awaiter(this, arguments, void 0, function* ({ id, fragment }) {
      if (!fragment) {
        fragment = { MedicationFields: MEDICATION_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_PRODUCTS = gql`
                  ${fValue}
                  query medicationProducts($id: String!) {
                    medicationProducts(id: $id) {
                      ...${fName}
                }
              }
                `;
      return makeQuery(this.apollo, GET_PRODUCTS, {
        id
      });
    });
  }
  /**
   * Retrieves all packages for a Medication PRODUCT
   * @param options - Query optiofns
   * @returns
   */
  getPackages(_a2) {
    return __awaiter(this, arguments, void 0, function* ({ id, fragment }) {
      if (!fragment) {
        fragment = { MedicationFields: MEDICATION_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_PACKAGES = gql`
                  ${fValue}
                  query medicationPackages($id: String!) {
                    medicationPackages(id: $id) {
                      ...${fName}
                }
              }
                `;
      return makeQuery(this.apollo, GET_PACKAGES, {
        id
      });
    });
  }
}
class ClinicalQueryManager {
  /**
   * @param apollo - An Apollo client instance
   */
  constructor(apollo) {
    this.catalog = new CatalogQueryManager(apollo);
    this.medication = new MedicationQueryManager(apollo);
    this.medicalEquipment = new MedicalEquipmentQueryManager(apollo);
    this.searchMedication = new SearchMedicationQueryManager(apollo);
    this.order = new OrderQueryManager(apollo);
    this.patient = new PatientQueryManager(apollo);
    this.pharmacy = new PharmacyQueryManager(apollo);
    this.prescription = new PrescriptionQueryManager(apollo);
    this.allergens = new AllergenQueryManager(apollo);
    this.prescriptionTemplate = new PrescriptionTemplateQueryManager(apollo);
  }
}
class ClientQueryManager {
  /**
   * @param apollo - An Apollo client instance
   */
  constructor(apollo) {
    this.apollo = apollo;
  }
  /**
   * Retrieves all Auth0 clients based on currently authenticated organization
   * @param options - Query options
   * @returns
   */
  getClients() {
    return __awaiter(this, arguments, void 0, function* ({ fragment } = {
      fragment: { ClientFields: CLIENT_FIELDS }
    }) {
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_CLIENTS = gql`
          ${fValue}
          query clients {
            clients {
              ...${fName}
            }
          }
        `;
      return makeQuery(this.apollo, GET_CLIENTS);
    });
  }
  /**
   * Rotates client_secret for the authenticated Auth0 organization
   * @param options - Query options
   * @returns
   */
  rotateSecret({ fragment }) {
    if (!fragment) {
      fragment = { ClientFields: CLIENT_FIELDS };
    }
    const [fName, fValue] = Object.entries(fragment)[0];
    const ROTATE_SECRET = gql`
      ${fValue}
      mutation rotateSecret(
        $id: ID!
      ) {
        rotateSecret(id: $id) {
          ...${fName}
        }
      }
    `;
    return makeMutation(this.apollo, ROTATE_SECRET);
  }
}
class OrgQueryManager {
  /**
   * @param apollo - An Apollo client instance
   */
  constructor(apollo) {
    this.apollo = apollo;
  }
  /**
   * Retrieves the organization associated with the authenticated user
   * @param options - Query options
   * @returns
   */
  getOrganization() {
    return __awaiter(this, arguments, void 0, function* ({ fragment } = {
      fragment: { OrganizationFields: ORGANIZATION_FIELDS }
    }) {
      if (!fragment) {
        fragment = { OrganizationFields: ORGANIZATION_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_ORGANIZATION = gql`
      ${fValue}
      query organization {
        organization {
          ...${fName}
        }
      }
    `;
      return makeQuery(this.apollo, GET_ORGANIZATION);
    });
  }
  /**
   * Retrieves all organizations associated with the auhenticated user
   * @param options - Query options
   * @returns
   */
  getOrganizations() {
    return __awaiter(this, arguments, void 0, function* ({ fragment } = {
      fragment: { OrganizationFields: ORGANIZATION_FIELDS }
    }) {
      if (!fragment) {
        fragment = { OrganizationFields: ORGANIZATION_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_ORGANIZATIONS = gql`
      ${fValue}
      query organizations {
        organizations {
          ...${fName}
        }
      }
    `;
      return makeQuery(this.apollo, GET_ORGANIZATIONS);
    });
  }
}
class WebhookQueryManager {
  /**
   * @param apollo - An Apollo client instance
   */
  constructor(apollo) {
    this.apollo = apollo;
  }
  /**
   * Retrieves list of webhook associated with currently authenticated organization
   * @param options - Query options
   * @returns
   */
  getWebhooks() {
    return __awaiter(this, arguments, void 0, function* ({ fragment } = {
      fragment: { WebhookFields: WEBHOOK_CONFIG_FIELDS }
    }) {
      if (!fragment) {
        fragment = { WebhookFields: WEBHOOK_CONFIG_FIELDS };
      }
      const [fName, fValue] = Object.entries(fragment)[0];
      const GET_WEBHOOKS = gql`
      ${fValue}
      query webhooks {
        webhooks {
          ...${fName}
        }
      }
    `;
      return makeQuery(this.apollo, GET_WEBHOOKS);
    });
  }
  /**
   * Creates a new webhook for the authenticated organization
   * @param options - Query options
   * @returns
   */
  createWebhook({ fragment }) {
    if (!fragment) {
      fragment = { WebhookFields: WEBHOOK_CONFIG_FIELDS };
    }
    const [fName, fValue] = Object.entries(fragment)[0];
    const CREATE_WEBHOOK = gql`
      ${fValue}
      mutation createWebhook(
        $name: String
        $filters: [String]
        $sharedSecret: String
        $url: String!
      ) {
        createWebhookConfig(name: $name, filters: $filters, sharedSecret: $sharedSecret, url: $url) {
          ...${fName}
        }
      }`;
    return makeMutation(this.apollo, CREATE_WEBHOOK);
  }
  /**
   * Deletes a webhook, by id, inside the currently authenticated organization
   * @returns
   */
  deleteWebhook() {
    const DELETE_WEBHOOK = gql`
      mutation deleteWebhookConfig($id: String!) {
        deleteWebhookConfig(id: $id)
      }
    `;
    return makeMutation(this.apollo, DELETE_WEBHOOK);
  }
}
class ManagementQueryManager {
  /**
   * @param apollo - An Apollo client instance
   */
  constructor(apollo) {
    this.client = new ClientQueryManager(apollo);
    this.organization = new OrgQueryManager(apollo);
    this.webhook = new WebhookQueryManager(apollo);
  }
}
const version$1 = "1.4.1";
const pkg = {
  version: version$1
};
var ConceptType;
(function(ConceptType2) {
  ConceptType2["Drug"] = "DRUG";
  ConceptType2["Package"] = "PACKAGE";
  ConceptType2["Product"] = "PRODUCT";
})(ConceptType || (ConceptType = {}));
var DiagnosisType;
(function(DiagnosisType2) {
  DiagnosisType2["Icd10"] = "ICD10";
})(DiagnosisType || (DiagnosisType = {}));
var FillState$1;
(function(FillState2) {
  FillState2["Canceled"] = "CANCELED";
  FillState2["New"] = "NEW";
  FillState2["Scheduled"] = "SCHEDULED";
  FillState2["Sent"] = "SENT";
})(FillState$1 || (FillState$1 = {}));
var FulfillmentType$1;
(function(FulfillmentType2) {
  FulfillmentType2["MailOrder"] = "MAIL_ORDER";
  FulfillmentType2["PickUp"] = "PICK_UP";
})(FulfillmentType$1 || (FulfillmentType$1 = {}));
var MedicationType$1;
(function(MedicationType2) {
  MedicationType2["Otc"] = "OTC";
  MedicationType2["Rx"] = "RX";
})(MedicationType$1 || (MedicationType$1 = {}));
var OrderState$1;
(function(OrderState2) {
  OrderState2["Canceled"] = "CANCELED";
  OrderState2["Completed"] = "COMPLETED";
  OrderState2["Error"] = "ERROR";
  OrderState2["Pending"] = "PENDING";
  OrderState2["Placed"] = "PLACED";
  OrderState2["Routing"] = "ROUTING";
})(OrderState$1 || (OrderState$1 = {}));
var OrgType$1;
(function(OrgType2) {
  OrgType2["Pharmacy"] = "PHARMACY";
  OrgType2["Prescriber"] = "PRESCRIBER";
})(OrgType$1 || (OrgType$1 = {}));
var PrescriptionState$1;
(function(PrescriptionState2) {
  PrescriptionState2["Draft"] = "DRAFT";
  PrescriptionState2["Active"] = "ACTIVE";
  PrescriptionState2["Depleted"] = "DEPLETED";
  PrescriptionState2["Expired"] = "EXPIRED";
  PrescriptionState2["Canceled"] = "CANCELED";
})(PrescriptionState$1 || (PrescriptionState$1 = {}));
var ScheduleType;
(function(ScheduleType2) {
  ScheduleType2["I"] = "I";
  ScheduleType2["Ii"] = "II";
  ScheduleType2["Iii"] = "III";
  ScheduleType2["Iv"] = "IV";
  ScheduleType2["V"] = "V";
})(ScheduleType || (ScheduleType = {}));
var SearchMedicationType;
(function(SearchMedicationType2) {
  SearchMedicationType2["Concept"] = "CONCEPT";
  SearchMedicationType2["Form"] = "FORM";
  SearchMedicationType2["Route"] = "ROUTE";
  SearchMedicationType2["Strength"] = "STRENGTH";
})(SearchMedicationType || (SearchMedicationType = {}));
var SexType$1;
(function(SexType2) {
  SexType2["Female"] = "FEMALE";
  SexType2["Male"] = "MALE";
  SexType2["Unknown"] = "UNKNOWN";
})(SexType$1 || (SexType$1 = {}));
const types = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  get ConceptType() {
    return ConceptType;
  },
  get DiagnosisType() {
    return DiagnosisType;
  },
  get FillState() {
    return FillState$1;
  },
  get FulfillmentType() {
    return FulfillmentType$1;
  },
  get MedicationType() {
    return MedicationType$1;
  },
  get OrderState() {
    return OrderState$1;
  },
  get OrgType() {
    return OrgType$1;
  },
  get PrescriptionState() {
    return PrescriptionState$1;
  },
  get ScheduleType() {
    return ScheduleType;
  },
  get SearchMedicationType() {
    return SearchMedicationType;
  },
  get SexType() {
    return SexType$1;
  }
}, Symbol.toStringTag, { value: "Module" }));
var BenefitType;
(function(BenefitType2) {
  BenefitType2["Coupon"] = "coupon";
  BenefitType2["Discount"] = "discount";
  BenefitType2["Insurance"] = "insurance";
})(BenefitType || (BenefitType = {}));
var ContactPreference;
(function(ContactPreference2) {
  ContactPreference2["Admin"] = "admin";
  ContactPreference2["Group"] = "group";
  ContactPreference2["Photon"] = "photon";
  ContactPreference2["Provider"] = "provider";
})(ContactPreference || (ContactPreference = {}));
var CoverageStatus;
(function(CoverageStatus2) {
  CoverageStatus2["Covered"] = "COVERED";
  CoverageStatus2["CoveredWithRestrictions"] = "COVERED_WITH_RESTRICTIONS";
  CoverageStatus2["NotCovered"] = "NOT_COVERED";
})(CoverageStatus || (CoverageStatus = {}));
var FillState;
(function(FillState2) {
  FillState2["Canceled"] = "CANCELED";
  FillState2["New"] = "NEW";
  FillState2["Scheduled"] = "SCHEDULED";
  FillState2["Sent"] = "SENT";
})(FillState || (FillState = {}));
var FulfillmentType;
(function(FulfillmentType2) {
  FulfillmentType2["MailOrder"] = "MAIL_ORDER";
  FulfillmentType2["PickUp"] = "PICK_UP";
})(FulfillmentType || (FulfillmentType = {}));
var MedicationType;
(function(MedicationType2) {
  MedicationType2["Otc"] = "OTC";
  MedicationType2["Rx"] = "RX";
})(MedicationType || (MedicationType = {}));
var OrderState;
(function(OrderState2) {
  OrderState2["Canceled"] = "CANCELED";
  OrderState2["Completed"] = "COMPLETED";
  OrderState2["Error"] = "ERROR";
  OrderState2["Pending"] = "PENDING";
  OrderState2["Placed"] = "PLACED";
  OrderState2["Routing"] = "ROUTING";
})(OrderState || (OrderState = {}));
var OrgType;
(function(OrgType2) {
  OrgType2["Pharmacy"] = "PHARMACY";
  OrgType2["Prescriber"] = "PRESCRIBER";
})(OrgType || (OrgType = {}));
var PrescriptionScreeningAlertSeverity;
(function(PrescriptionScreeningAlertSeverity2) {
  PrescriptionScreeningAlertSeverity2["Major"] = "MAJOR";
  PrescriptionScreeningAlertSeverity2["Minor"] = "MINOR";
  PrescriptionScreeningAlertSeverity2["Moderate"] = "MODERATE";
})(PrescriptionScreeningAlertSeverity || (PrescriptionScreeningAlertSeverity = {}));
var PrescriptionScreeningAlertType;
(function(PrescriptionScreeningAlertType2) {
  PrescriptionScreeningAlertType2["Allergen"] = "ALLERGEN";
  PrescriptionScreeningAlertType2["Drug"] = "DRUG";
})(PrescriptionScreeningAlertType || (PrescriptionScreeningAlertType = {}));
var PrescriptionState;
(function(PrescriptionState2) {
  PrescriptionState2["Active"] = "ACTIVE";
  PrescriptionState2["Canceled"] = "CANCELED";
  PrescriptionState2["Depleted"] = "DEPLETED";
  PrescriptionState2["Draft"] = "DRAFT";
  PrescriptionState2["Expired"] = "EXPIRED";
})(PrescriptionState || (PrescriptionState = {}));
var RoutingConstraintType;
(function(RoutingConstraintType2) {
  RoutingConstraintType2["Exclude"] = "EXCLUDE";
  RoutingConstraintType2["Include"] = "INCLUDE";
  RoutingConstraintType2["NoAdvice"] = "NO_ADVICE";
  RoutingConstraintType2["NoRouting"] = "NO_ROUTING";
})(RoutingConstraintType || (RoutingConstraintType = {}));
var SexType;
(function(SexType2) {
  SexType2["Female"] = "FEMALE";
  SexType2["Male"] = "MALE";
  SexType2["Unknown"] = "UNKNOWN";
})(SexType || (SexType = {}));
var SortByDirection;
(function(SortByDirection2) {
  SortByDirection2["Asc"] = "ASC";
  SortByDirection2["Desc"] = "DESC";
})(SortByDirection || (SortByDirection = {}));
var SortByOption;
(function(SortByOption2) {
  SortByOption2["OrderCreatedAt"] = "ORDER_CREATED_AT";
  SortByOption2["OrderReadyByTime"] = "ORDER_READY_BY_TIME";
})(SortByOption || (SortByOption = {}));
const SupervisorCardFragmentDoc = { "kind": "Document", "definitions": [{ "kind": "FragmentDefinition", "name": { "kind": "Name", "value": "SupervisorCard" }, "typeCondition": { "kind": "NamedType", "name": { "kind": "Name", "value": "Supervisor" } }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "firstName" } }, { "kind": "Field", "name": { "kind": "Name", "value": "lastName" } }, { "kind": "Field", "name": { "kind": "Name", "value": "npi" } }] } }] };
const CreateSupervisorMutationDocument = { "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "mutation", "name": { "kind": "Name", "value": "CreateSupervisorMutation" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "firstName" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "lastName" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "npi" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "createSupervisor" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "input" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "firstName" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "firstName" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "lastName" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "lastName" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "npi" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "npi" } } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "FragmentSpread", "name": { "kind": "Name", "value": "SupervisorCard" } }] } }] } }, { "kind": "FragmentDefinition", "name": { "kind": "Name", "value": "SupervisorCard" }, "typeCondition": { "kind": "NamedType", "name": { "kind": "Name", "value": "Supervisor" } }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "firstName" } }, { "kind": "Field", "name": { "kind": "Name", "value": "lastName" } }, { "kind": "Field", "name": { "kind": "Name", "value": "npi" } }] } }] };
const SearchTreatmentsDocument = { "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "SearchTreatments" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "filter" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "TreatmentFilter" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "treatments" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "filter" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "filter" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }] } }] } }] };
const SearchTreatmentOptionsQueryDocument = { "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "SearchTreatmentOptionsQuery" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "filter" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "TreatmentFilter" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "treatments" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "filter" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "filter" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "__typename" } }, { "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }] } }] } }] };
const MeUserQueryDocument = { "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "MeUserQuery" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "me" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "credentials" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "state" } }] } }] } }] } }] };
const SupervisorCardQueryDocument = { "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "SupervisorCardQuery" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "supervisors" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "FragmentSpread", "name": { "kind": "Name", "value": "SupervisorCard" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "mostRecentSupervisor" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }] } }] } }, { "kind": "FragmentDefinition", "name": { "kind": "Name", "value": "SupervisorCard" }, "typeCondition": { "kind": "NamedType", "name": { "kind": "Name", "value": "Supervisor" } }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "firstName" } }, { "kind": "Field", "name": { "kind": "Name", "value": "lastName" } }, { "kind": "Field", "name": { "kind": "Name", "value": "npi" } }] } }] };
const documents = {
  "\n  fragment SupervisorCard on Supervisor {\n    id\n    firstName\n    lastName\n    npi\n  }\n": SupervisorCardFragmentDoc,
  "\n  mutation CreateSupervisorMutation($firstName: String!, $lastName: String!, $npi: String!) {\n    createSupervisor(input: { firstName: $firstName, lastName: $lastName, npi: $npi }) {\n      ...SupervisorCard\n    }\n  }\n": CreateSupervisorMutationDocument,
  "\n  query SearchTreatments($filter: TreatmentFilter!) {\n    treatments(filter: $filter) {\n      id\n      name\n    }\n  }\n": SearchTreatmentsDocument,
  "\n  query SearchTreatmentOptionsQuery($filter: TreatmentFilter!) {\n    treatments(filter: $filter) {\n      __typename\n      id\n      name\n    }\n  }\n": SearchTreatmentOptionsQueryDocument,
  "\n  query MeUserQuery {\n    me {\n      credentials\n      address {\n        state\n      }\n    }\n  }\n": MeUserQueryDocument,
  "\n  query SupervisorCardQuery {\n    supervisors {\n      ...SupervisorCard\n    }\n    mostRecentSupervisor {\n      id\n    }\n  }\n": SupervisorCardQueryDocument
};
function graphql(source) {
  var _a2;
  return (_a2 = documents[source]) !== null && _a2 !== void 0 ? _a2 : {};
}
const SearchTreatmentsQuery = graphql(`
  query SearchTreatments($filter: TreatmentFilter!) {
    treatments(filter: $filter) {
      id
      name
    }
  }
`);
const SearchTreatmentOptionsQuery = graphql(`
  query SearchTreatmentOptionsQuery($filter: TreatmentFilter!) {
    treatments(filter: $filter) {
      __typename
      id
      name
    }
  }
`);
const MeUserQuery = graphql(`
  query MeUserQuery {
    me {
      credentials
      address {
        state
      }
    }
  }
`);
const SupervisorCardQuery = graphql(`
  query SupervisorCardQuery {
    supervisors {
      ...SupervisorCard
    }
    mostRecentSupervisor {
      id
    }
  }
`);
const CreateSupervisorMutation = graphql(`
  mutation CreateSupervisorMutation($firstName: String!, $lastName: String!, $npi: String!) {
    createSupervisor(input: { firstName: $firstName, lastName: $lastName, npi: $npi }) {
      ...SupervisorCard
    }
  }
`);
var _a;
const version = (_a = pkg === null || pkg === void 0 ? void 0 : pkg.version) !== null && _a !== void 0 ? _a : "unknown";
class PhotonClient {
  /**
   * Constructs a new PhotonSDK instance
   * @param config - Photon SDK configuration options
   * @remarks - Note, that organization is optional for scenarios in which a provider supports more than themselves.
   */
  constructor({ domain, clientId, redirectURI, organization, env = "photon", audience, connection, uri, developmentMode = false }, elementsVersion) {
    this.audience = audience ? audience : lambdasApiUrl[env];
    this.uri = uri ? uri : `${lambdasApiUrl[env]}/graphql`;
    this.clinicalUrl = uri ? getClinicalUrl(uri) : clinicalAppUrl[env];
    this.clinicalApiUri = `${clinicalApiUrl[env]}/graphql`;
    if (developmentMode) {
      this.audience = lambdasApiUrl["neutron"];
      this.uri = `${lambdasApiUrl["neutron"]}/graphql`;
      this.clinicalApiUri = `${clinicalApiUrl["neutron"]}/graphql`;
    }
    const params = {
      domain: domain ? domain : developmentMode ? "auth.neutron.health" : "auth.photon.health",
      clientId,
      cacheLocation: "localstorage",
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      authorizationParams: Object.assign({ redirect_uri: redirectURI, audience: this.audience }, connection ? { connection } : {})
    };
    this.auth0Client = new Auth0Client(params);
    this.organization = organization;
    this.authentication = new AuthManager(Object.assign({ authentication: this.auth0Client, organization: this.organization, audience: this.audience }, connection ? { connection } : {}));
    this.apollo = this.constructApolloClient({ elementsVersion, isServices: false });
    this.apolloClinical = this.constructApolloClient({ elementsVersion, isServices: true });
    this.clinical = new ClinicalQueryManager(this.apollo);
    this.management = new ManagementQueryManager(this.apollo);
  }
  constructApolloClient({ elementsVersion, isServices } = {
    isServices: false
  }) {
    const apollo = new ApolloClient({
      link: setContext((_, _a2) => __awaiter(this, void 0, void 0, function* () {
        var { headers: baseHeaders } = _a2, rest = __rest(_a2, ["headers"]);
        let token;
        try {
          token = yield this.authentication.getAccessToken();
        } catch (_b) {
        }
        const headers = Object.assign(Object.assign(Object.assign({}, baseHeaders), { "x-photon-sdk-version": version }), elementsVersion ? { "x-photon-elements-version": elementsVersion } : {});
        if (!token) {
          return Object.assign({ headers }, rest);
        }
        return Object.assign(Object.assign({}, rest), { headers: isServices ? Object.assign(Object.assign({}, headers), { "x-photon-auth-token": token, "x-photon-auth-token-type": "auth0" }) : Object.assign(Object.assign({}, headers), { authorization: token }) });
      })).concat(new HttpLink({
        uri: isServices ? this.clinicalApiUri : this.uri
      })),
      defaultOptions: {
        query: {
          fetchPolicy: "cache-first",
          errorPolicy: "all"
        },
        mutate: {
          // Because of the way we do lambdas, we have a _slight_ delay between reads and writes
          // This forces a delay of 1s which isn't the greatest, but also isn't the worst
          // https://github.com/apollographql/apollo-feature-requests/issues/207
          onQueryUpdated: delayRefetchedQuery
        }
      },
      cache: new InMemoryCache({
        // Addresses and names don't have unique ids. So we just allow for merging these
        typePolicies: {
          Patient: {
            fields: {
              name: {
                merge(existing, incoming, { mergeObjects }) {
                  return mergeObjects(existing, incoming);
                }
              },
              address: {
                merge(existing, incoming, { mergeObjects }) {
                  return mergeObjects(existing, incoming);
                }
              }
            }
          },
          User: {
            fields: {
              name: {
                merge(existing, incoming, { mergeObjects }) {
                  return mergeObjects(existing, incoming);
                }
              },
              address: {
                merge(existing, incoming, { mergeObjects }) {
                  return mergeObjects(existing, incoming);
                }
              }
            }
          }
        }
      })
    });
    return apollo;
  }
  /**
   * Sets the organization ID to use
   * @returns PhotonSDK
   */
  setOrganization(organizationId) {
    this.organization = organizationId;
    this.authentication = new AuthManager({
      authentication: this.auth0Client,
      organization: organizationId,
      audience: this.audience
    });
    return this;
  }
  /**
   * Clears the organization ID
   * @returns PhotonSDK
   */
  clearOrganization() {
    this.organization = void 0;
    this.authentication = new AuthManager({
      authentication: this.auth0Client,
      organization: void 0,
      audience: this.audience
    });
    this.authentication.login({});
    return this;
  }
}
const wait = (ms) => new Promise((res) => setTimeout(res, ms));
const delayRefetchedQuery = (observableQuery) => __awaiter(void 0, void 0, void 0, function* () {
  yield wait(1e3);
  observableQuery.refetch();
});
export {
  CreateSupervisorMutation,
  MeUserQuery,
  PhotonClient,
  SearchTreatmentOptionsQuery,
  SearchTreatmentsQuery,
  SupervisorCardQuery,
  fragments,
  graphql,
  types
};
